import { FC, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	Button,
	Card,
	Divider,
	Empty,
	Input,
	message,
	Spin,
	Table,
	Tag,
	Tooltip
} from 'antd'
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DownloadOutlined,
	ThunderboltOutlined,
	ToolOutlined,
	UserOutlined
} from '@ant-design/icons'
import { PageBreadcrumb } from '../../components/PageBreadcrumb'
import { relayStore } from '../../store/relay'
import { authStore } from '../../store/auth'
import {
	ElectricMechanic,
	ElectricMechanicValue,
	Record as RecordData
} from '../../helpers/types/relay'
import {
	updateRecordStatus,
	fetchRecordPdfUrl
} from '../../api/relay'
import {
	RNElectric,
	RNMechanic,
	RNRelayOneRecords,
	RNRelayType
} from '../../helpers/routeNames'

const STATUS_MAP: Record<
	string,
	{ color: string; label: string; icon: React.ReactNode }
> = {
	pending: {
		color: 'default',
		label: 'Ожидание',
		icon: <ClockCircleOutlined />
	},
	mechanic: {
		color: 'processing',
		label: 'Механика',
		icon: <ToolOutlined />
	},
	electric: {
		color: 'warning',
		label: 'Электрика',
		icon: <ThunderboltOutlined />
	},
	approved: {
		color: 'success',
		label: 'Утверждено',
		icon: <CheckCircleOutlined />
	},
	rejected: {
		color: 'error',
		label: 'Отклонено',
		icon: <CloseCircleOutlined />
	}
}

function formatUser(user: RecordData['createdByUser']): string {
	if (!user) return '—'
	return [user.lastName, user.firstName, user.middleName]
		.filter(Boolean)
		.join(' ')
}

export const RelayRecord: FC = () => {
	const { relayOneId, recordId, relayId, relayTypeId } = useParams<{
		relayTypeId: string
		relayId: string
		relayOneId: string
		recordId: string
	}>()
	const {
		relayTypes,
		fetchRelayOneRecordOne,
		checkRecordMechanicValue,
		checkRecordElectricValue
	} = relayStore()
	const { user } = authStore()
	const navigate = useNavigate()

	const [record, setRecord] = useState<RecordData | null>(null)
	const [isMDuplicate, setIsMDuplicate] = useState(false)
	const [isEDuplicate, setIsEDuplicate] = useState(false)
	const [loading, setLoading] = useState(true)
	const [rejectionReason, setRejectionReason] = useState('')
	const [actionLoading, setActionLoading] = useState(false)
	const [pdfUrl, setPdfUrl] = useState<string | null>(null)
	const [pdfLoading, setPdfLoading] = useState(false)

	const canApprove =
		user?.role === 'admin' || user?.role === 'supervisor'

	const handleApprove = async () => {
		if (!recordId) return
		setActionLoading(true)
		try {
			const updated = await updateRecordStatus(recordId, {
				status: 'approved'
			})
			setRecord((prev) =>
				prev ? { ...prev, ...updated } : prev
			)
			// Fetch PDF URL generated on approval
			setPdfLoading(true)
			try {
				const url = await fetchRecordPdfUrl(recordId)
				if (url) setPdfUrl(url)
			} catch {
				// PDF not ready yet — not a blocking error
			} finally {
				setPdfLoading(false)
			}
			message.success('Запись утверждена')
		} catch {
			message.error('Не удалось утвердить запись')
		} finally {
			setActionLoading(false)
		}
	}

	const handleReject = async () => {
		if (!recordId || !rejectionReason.trim()) return
		setActionLoading(true)
		try {
			const updated = await updateRecordStatus(recordId, {
				status: 'rejected',
				rejectionReason: rejectionReason.trim()
			})
			setRecord((prev) =>
				prev ? { ...prev, ...updated } : prev
			)
			setRejectionReason('')
			message.success('Запись отклонена')
		} catch {
			message.error('Не удалось отклонить запись')
		} finally {
			setActionLoading(false)
		}
	}

	const relayType = useMemo(
		() => relayTypes.find((rt) => rt.id === relayTypeId),
		[relayTypes, relayTypeId]
	)
	const relay = useMemo(
		() => relayType?.relays.find((r) => r.id === relayId),
		[relayType, relayId]
	)

	useEffect(() => {
		if (!relayTypeId || !relayId || !relayOneId || !recordId)
			return
		setLoading(true)
		Promise.all([
			fetchRelayOneRecordOne(
				relayTypeId,
				relayId,
				relayOneId,
				recordId
			),
			checkRecordMechanicValue(
				relayTypeId,
				relayId,
				relayOneId,
				recordId
			),
			checkRecordElectricValue(
				relayTypeId,
				relayId,
				relayOneId,
				recordId
			)
		])
			.then(([rec, mCheck, eCheck]) => {
				setRecord(rec)
				setIsMDuplicate(mCheck.dublicate)
				setIsEDuplicate(eCheck.dublicate)
				if (rec.status === 'approved' && recordId) {
					setPdfLoading(true)
					fetchRecordPdfUrl(recordId)
						.then((url) => {
							if (url) setPdfUrl(url)
						})
						.catch(() => {})
						.finally(() => setPdfLoading(false))
				}
			})
			.finally(() => setLoading(false))
	}, [
		fetchRelayOneRecordOne,
		checkRecordMechanicValue,
		checkRecordElectricValue,
		relayTypeId,
		relayId,
		relayOneId,
		recordId
	])

	if (loading) {
		return (
			<div style={{ textAlign: 'center', padding: 80 }}>
				<Spin size="large" />
			</div>
		)
	}

	if (!record) {
		return (
			<Empty
				description="Запись не найдена"
				style={{ padding: 80 }}
			/>
		)
	}

	const status = record.status || 'pending'
	const statusInfo = STATUS_MAP[status] || STATUS_MAP.pending
	const basePath = `${RNRelayOneRecords}/${relayTypeId}/${relayId}/${relayOneId}`
	const mechanicPath = `${RNMechanic}/${relayTypeId}/${relayId}/${relayOneId}/${recordId}`
	const electricPath = `${RNElectric}/${relayTypeId}/${relayId}/${relayOneId}/${recordId}`

	const hasMechanic =
		record.mechanicValues.length > 0 && isMDuplicate
	const hasElectric =
		record.electricValues.length > 0 || isEDuplicate

	return (
		<div style={{ padding: '16px', paddingBottom: '90px' }}>
			<PageBreadcrumb
				items={[
					{
						label: relayType?.name || 'Тип реле',
						path: RNRelayType
					},
					{
						label: relay?.name || 'Записи',
						path: basePath
					},
					{ label: 'Запись' }
				]}
			/>

			{/* ── Record info card ────────────────────────── */}
			<Card
				style={{
					borderRadius: 12,
					boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
					marginBottom: 20
				}}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 12,
						marginBottom: 12
					}}>
					<h2 style={{ margin: 0, fontSize: 20 }}>
						Информация о записи
					</h2>
					<Tag
						icon={statusInfo.icon}
						color={statusInfo.color}
						style={{
							fontSize: 14,
							padding: '4px 12px'
						}}>
						{statusInfo.label}
					</Tag>
				</div>
				<Divider style={{ margin: '8px 0 16px' }} />
				<div
					style={{
						display: 'grid',
						gridTemplateColumns:
							'repeat(auto-fill, minmax(220px, 1fr))',
						gap: 16
					}}>
					<InfoCell
						label="Дата создания"
						value={new Date(
							record.createdAt || 0
						).toLocaleString('ru')}
					/>
					<InfoCell
						label="Создал"
						value={formatUser(record.createdByUser)}
						icon={<UserOutlined />}
					/>
					{record.mechanicByUser && (
						<InfoCell
							label="Механик"
							value={formatUser(record.mechanicByUser)}
							icon={<ToolOutlined />}
						/>
					)}
					{record.electricByUser && (
						<InfoCell
							label="Электрик"
							value={formatUser(record.electricByUser)}
							icon={<ThunderboltOutlined />}
						/>
					)}
					{record.approvedByUser && (
						<InfoCell
							label="Утвердил"
							value={formatUser(record.approvedByUser)}
							icon={<CheckCircleOutlined />}
						/>
					)}
				</div>
			</Card>

			{/* ── Rejection reason ────────────────────────── */}
			{record.status === 'rejected' &&
				record.rejectionReason && (
					<Card
						style={{
							borderRadius: 12,
							marginBottom: 20,
							borderColor: '#ff4d4f',
							background: '#fff2f0'
						}}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								marginBottom: 8
							}}>
							<CloseCircleOutlined
								style={{
									color: '#ff4d4f',
									fontSize: 16
								}}
							/>
							<strong style={{ color: '#ff4d4f' }}>
								Причина отклонения
							</strong>
						</div>
						<p style={{ margin: 0, color: '#5c0011' }}>
							{record.rejectionReason}
						</p>
					</Card>
				)}

			{/* ── PDF download (approved records) ─────────── */}
			{record.status === 'approved' && (
				<Card
					style={{
						borderRadius: 12,
						marginBottom: 20,
						borderColor: '#52c41a',
						background: '#f6ffed'
					}}>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8
							}}>
							<CheckCircleOutlined
								style={{
									color: '#52c41a',
									fontSize: 16
								}}
							/>
							<strong style={{ color: '#135200' }}>
								Запись утверждена
							</strong>
						</div>
						{pdfUrl ? (
							<Button
								type="primary"
								icon={<DownloadOutlined />}
								onClick={() =>
									window.open(pdfUrl, '_blank')
								}>
								Скачать PDF
							</Button>
						) : (
							<Button
								icon={<DownloadOutlined />}
								loading={pdfLoading}
								disabled={pdfLoading}
								onClick={async () => {
									if (!recordId) return
									setPdfLoading(true)
									try {
										const url =
											await fetchRecordPdfUrl(
												recordId
											)
										if (url) {
											setPdfUrl(url)
											window.open(url, '_blank')
										} else {
											message.info(
												'PDF ещё не готов'
											)
										}
									} catch {
										message.error(
											'Не удалось получить PDF'
										)
									} finally {
										setPdfLoading(false)
									}
								}}>
								{pdfLoading ? 'Загрузка...' : 'PDF'}
							</Button>
						)}
					</div>
				</Card>
			)}

			{/* ── Approval panel (admin only, electric status) ── */}
			{canApprove && record.status === 'electric' && (
				<Card
					style={{
						borderRadius: 12,
						marginBottom: 20,
						borderColor: '#1677ff',
						background: '#f0f7ff'
					}}>
					<h3
						style={{
							margin: '0 0 16px',
							fontSize: 16,
							color: '#142D49'
						}}>
						Решение по записи
					</h3>
					<Button
						type="primary"
						icon={<CheckCircleOutlined />}
						loading={actionLoading}
						onClick={handleApprove}
						style={{ marginBottom: 16 }}>
						Утвердить
					</Button>
					<Divider style={{ margin: '0 0 16px' }} />
					<Input.TextArea
						rows={3}
						placeholder="Причина отклонения..."
						value={rejectionReason}
						onChange={(e) =>
							setRejectionReason(e.target.value)
						}
						style={{ marginBottom: 8 }}
					/>
					<Button
						danger
						icon={<CloseCircleOutlined />}
						loading={actionLoading}
						disabled={!rejectionReason.trim()}
						onClick={handleReject}>
						Отклонить
					</Button>
				</Card>
			)}

			{/* ── Mechanical characteristics ───────────────── */}
			<SectionCard
				title="Механические характеристики"
				icon={
					<ToolOutlined
						style={{ color: 'var(--default)' }}
					/>
				}>
				{hasMechanic ? (
					<CharacteristicsTable
						values={record.mechanicValues}
						definitions={relayType?.mechanics || []}
						idField="mechanicId"
					/>
				) : (
					<div style={{ textAlign: 'center' }}>
						<p
							style={{
								color: '#999',
								margin: '0 0 12px'
							}}>
							Механические значения ещё не заполнены
						</p>

						{!canApprove && (
							<Button
								type="primary"
								icon={<ToolOutlined />}
								onClick={() =>
									navigate(mechanicPath)
								}>
								Заполнить механику
							</Button>
						)}
					</div>
				)}
			</SectionCard>

			{/* ── Electrical characteristics ────────────────── */}
			<SectionCard
				title="Электрические характеристики"
				icon={
					<ThunderboltOutlined
						style={{ color: '#faad14' }}
					/>
				}>
				{hasElectric ? (
					<CharacteristicsTable
						values={record.electricValues}
						definitions={relayType?.electrics || []}
						idField="electricId"
					/>
				) : (
					<div style={{ textAlign: 'center' }}>
						<p
							style={{
								color: '#999',
								margin: '0 0 12px'
							}}>
							Электрические значения ещё не заполнены
						</p>
						{!canApprove && (
							<Tooltip
								title={
									!hasMechanic
										? 'Сначала заполните механические характеристики'
										: undefined
								}>
								<Button
									type="primary"
									icon={<ThunderboltOutlined />}
									disabled={!hasMechanic}
									onClick={() =>
										navigate(electricPath)
									}>
									Заполнить электрику
								</Button>
							</Tooltip>
						)}
					</div>
				)}
			</SectionCard>
		</div>
	)
}

// ── Sub-components ────────────────────────────────────────────

const InfoCell: FC<{
	label: string
	value: string
	icon?: React.ReactNode
}> = ({ label, value, icon }) => (
	<div
		style={{
			padding: 12,
			borderRadius: 8,
			background: 'rgba(56, 148, 235, 0.05)'
		}}>
		<div
			style={{
				fontSize: 12,
				color: 'var(--gray, #bcc6d8)',
				textTransform: 'uppercase',
				letterSpacing: 0.5,
				fontWeight: 500,
				marginBottom: 4
			}}>
			{label}
		</div>
		<div
			style={{
				fontSize: 15,
				fontWeight: 600,
				color: 'var(--dark-text-color, #142D49)',
				display: 'flex',
				alignItems: 'center',
				gap: 6
			}}>
			{icon}
			{value}
		</div>
	</div>
)

const SectionCard: FC<{
	title: string
	icon: React.ReactNode
	children: React.ReactNode
}> = ({ title, icon, children }) => (
	<Card
		style={{
			borderRadius: 12,
			boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
			marginBottom: 20
		}}>
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 10,
				marginBottom: 16
			}}>
			{icon}
			<h3
				style={{
					margin: 0,
					fontSize: 17,
					fontWeight: 600,
					color: 'var(--dark-text-color, #142D49)'
				}}>
				{title}
			</h3>
		</div>
		{children}
	</Card>
)

/**
 * Grouped table that respects parent→child hierarchy of characteristics.
 * Groups by parent, shows children as rows under a group header.
 */
const CharacteristicsTable: FC<{
	values: ElectricMechanicValue[]
	definitions: ElectricMechanic[]
	idField: 'mechanicId' | 'electricId'
}> = ({ values, definitions, idField }) => {
	const rows = useMemo(() => {
		// Group definitions by parentId
		const grouped = new Map<string | null, ElectricMechanic[]>()
		for (const def of definitions) {
			if (def.isAcceptsValue) {
				const key = def.parentId || null
				const arr = grouped.get(key) || []
				arr.push(def)
				grouped.set(key, arr)
			}
		}

		// Sort each group by orderInLevel
		Array.from(grouped.values()).forEach((arr) =>
			arr.sort(
				(a: ElectricMechanic, b: ElectricMechanic) =>
					a.orderInLevel - b.orderInLevel
			)
		)

		// Build flat row list with group headers
		interface TableRow {
			key: string
			isGroupHeader: boolean
			groupName?: string
			name: string
			value: string
			measurement: string
		}

		const result: TableRow[] = []
		const valueMap = new Map(values.map((v) => [v[idField], v]))

		// Top-level parents (characters that have children)
		const parentIds = new Set(
			definitions
				.filter((d) => d.parentId)
				.map((d) => d.parentId!)
		)

		// Characters without parents that are also not parents themselves → standalone
		const topLevel = definitions.filter(
			(d) =>
				!d.parentId &&
				d.isAcceptsValue &&
				!parentIds.has(d.id)
		)

		// Standalone items first
		for (const def of topLevel.sort(
			(a, b) => a.orderInLevel - b.orderInLevel
		)) {
			const val = valueMap.get(def.id)
			result.push({
				key: def.id,
				isGroupHeader: false,
				name: def.name,
				value: val?.value || '—',
				measurement: def.measurement || ''
			})
		}

		// Grouped items (parent → children)
		const parents = definitions
			.filter((d) => parentIds.has(d.id))
			.sort((a, b) => a.orderInLevel - b.orderInLevel)

		for (const parent of parents) {
			result.push({
				key: `group-${parent.id}`,
				isGroupHeader: true,
				groupName: parent.name,
				name: '',
				value: '',
				measurement: ''
			})
			const children = grouped.get(parent.id) || []
			for (const child of children) {
				const val = valueMap.get(child.id)
				result.push({
					key: child.id,
					isGroupHeader: false,
					name: child.name,
					value: val?.value || '—',
					measurement: child.measurement || ''
				})
			}
		}

		return result
	}, [values, definitions, idField])

	const columns = [
		{
			title: 'Характеристика',
			dataIndex: 'name',
			key: 'name',
			render: (text: string, row: (typeof rows)[0]) =>
				row.isGroupHeader ? (
					<strong
						style={{
							color: 'var(--default, #3894EB)',
							fontSize: 14
						}}>
						{row.groupName}
					</strong>
				) : (
					<span
						style={{
							paddingLeft: row.isGroupHeader ? 0 : 8
						}}>
						{text}
					</span>
				)
		},
		{
			title: 'Значение',
			dataIndex: 'value',
			key: 'value',
			width: 140,
			render: (text: string, row: (typeof rows)[0]) =>
				row.isGroupHeader ? null : <strong>{text}</strong>
		},
		{
			title: 'Ед. изм.',
			dataIndex: 'measurement',
			key: 'measurement',
			width: 100,
			render: (text: string, row: (typeof rows)[0]) =>
				row.isGroupHeader ? null : (
					<span style={{ color: '#999' }}>{text}</span>
				)
		}
	]

	return (
		<Table
			dataSource={rows}
			columns={columns}
			pagination={false}
			size="small"
			rowClassName={(row) =>
				row.isGroupHeader ? 'group-header-row' : ''
			}
			style={{ marginTop: 4 }}
		/>
	)
}
